import { ReactElement, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Tooltip } from '@mui/material';
import { Visibility as ShowIcon, Edit as EditIcon } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowDown as ExportListIcon } from '@fortawesome/free-solid-svg-icons';

import { RouteItemStateType } from '~globals/types/enums';
import { ItemExtended } from '~services/item/types';
import { useAppSelector } from '~hooks/useAppSelector';
import { useDownloadEvidencesItemPDF } from '~hooks/useDownloadEvidencesItemPDF';
import { selectAuth } from '~store/slices/auth';
import { isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { replaceParamsPath } from '~utils/router';
import { isRouteStopOrderFinalized } from '~utils/route';
import { PATHS } from '~constants/paths';

import { useDashboardVariantV2GiverContentFinalizedTabContext } from '../../Context';
import { ITEM_KEY_ID } from '../../utils';

import { DashboardVariantV2GiverContentFinalizedTabDataTableIconButton } from './styles';

export interface DashboardVariantV2GiverContentFinalizedTabDataTableActionsProps {
  data: ItemExtended;
}

const DashboardVariantV2GiverContentFinalizedTabDataTableActions = ({
  data,
}: DashboardVariantV2GiverContentFinalizedTabDataTableActionsProps): ReactElement => {
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuth);

  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const {
    dialogs: {
      feedback: { handleOpen: handleOpenDialogFeedback },
      detailsOrder: { handleOpen: handleOpenDialogDetailsOrder },
    },
  } = useDashboardVariantV2GiverContentFinalizedTabContext();

  const handleShow = useCallback(() => {
    if (!!data.routeCode && !!data.hasFeedback && !!data.routeItemId) {
      handleOpenDialogFeedback({
        stopId: data.routeItemId as string,
        orderId: data[ITEM_KEY_ID],
      });

      return;
    }

    handleOpenDialogDetailsOrder({
      orderId: data[ITEM_KEY_ID],
      currentTab: null,
    });
  }, [data, handleOpenDialogFeedback, handleOpenDialogDetailsOrder]);

  const enabledEdit = useMemo(
    () => !isUserOnlyReader && !isUserOnlyCreatorOrder && data.canModify,
    [isUserOnlyReader, isUserOnlyCreatorOrder, data.canModify],
  );

  const handleEdit = useCallback(() => {
    navigate(
      replaceParamsPath(PATHS.ORDERS.EDIT, { ':orderId': data[ITEM_KEY_ID] }),
    );
  }, [data, navigate]);

  const enabledDownloadDeliveryTicket = useMemo(() => {
    if (!data.routeCode) return false;

    return isRouteStopOrderFinalized(
      data.itemStateTypeId as RouteItemStateType,
    );
  }, [data.itemStateTypeId, data.routeCode]);

  const {
    loading: loadingDowloadEvidencesOrderPDF,
    onDownloadPDF: handleDowloadEvidencesOrderPDF,
  } = useDownloadEvidencesItemPDF();

  const handleDownloadDeliveryTicket = useCallback(() => {
    handleDowloadEvidencesOrderPDF(data.trackingCode);
  }, [data.trackingCode, handleDowloadEvidencesOrderPDF]);

  return (
    <Stack direction="row" spacing={0.75} justifyContent="center">
      <Tooltip title="Ver" arrow>
        <DashboardVariantV2GiverContentFinalizedTabDataTableIconButton
          onClick={handleShow}
          color="success"
        >
          <ShowIcon />
        </DashboardVariantV2GiverContentFinalizedTabDataTableIconButton>
      </Tooltip>

      {enabledEdit && (
        <Tooltip title="Editar" arrow>
          <DashboardVariantV2GiverContentFinalizedTabDataTableIconButton
            color="info"
            variant="outlined"
            onClick={handleEdit}
          >
            <EditIcon />
          </DashboardVariantV2GiverContentFinalizedTabDataTableIconButton>
        </Tooltip>
      )}

      {enabledDownloadDeliveryTicket && (
        <Tooltip title="Descargar comprobante de visita" arrow>
          <DashboardVariantV2GiverContentFinalizedTabDataTableIconButton
            color="secondary"
            variant="outlined"
            onClick={handleDownloadDeliveryTicket}
            loading={loadingDowloadEvidencesOrderPDF}
          >
            <FontAwesomeIcon icon={ExportListIcon} />
          </DashboardVariantV2GiverContentFinalizedTabDataTableIconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default memo(DashboardVariantV2GiverContentFinalizedTabDataTableActions);
